import { useNavigate } from 'react-router-dom'
import { useLocale } from '@/hooks/useLocale'
import Card from '@/components/ui/Card'
import { cn } from '@/utils/cn'
import { Book, Headphones } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface Surah {
  id: number
  number: number
  name: string
  nameEn: string
  ayahs: number
  revelationOrder: number
  juz: number
}

interface SurahCardProps {
  surah: Surah
  isContinueReading?: boolean
  selectedReciterId?: string | null
  onCardClick?: () => void
  onListen?: () => void
}

export default function SurahCard({ 
  surah, 
  isContinueReading = false,
  selectedReciterId = null,
  onCardClick,
  onListen
}: SurahCardProps) {
  const navigate = useNavigate()
  const { localizePath } = useLocale()
  const { t } = useTranslation()

  const handleRead = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(localizePath(`/quran/${surah.number}`))
  }

  const handleListen = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Always use the callback if provided (for in-page audio player)
    if (onListen) {
      onListen()
      return
    }
    // Only navigate if no callback is provided (fallback for other pages)
    if (selectedReciterId) {
      navigate(localizePath(`/quran/listen?surah=${surah.number}&reciter=${selectedReciterId}`))
    } else {
      navigate(localizePath(`/quran/listen?surah=${surah.number}`))
    }
  }

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick()
    } else {
      // Default: navigate to reading page
      navigate(localizePath(`/quran/${surah.number}`))
    }
  }

  return (
    <Card
      className={cn(
        'hover:shadow-lg transition-all cursor-pointer',
        isContinueReading && 'border-2 border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/30'
      )}
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0',
            isContinueReading
              ? 'bg-primary-600 dark:bg-primary-500 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          )}
        >
          {surah.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{surah.name}</h3>
            {isContinueReading && (
              <span className="text-xs bg-primary-600 dark:bg-primary-500 text-white px-2 py-1 rounded">
                {t('quran.continueReading', 'متابعة القراءة')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{surah.nameEn}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>{surah.ayahs} {t('quran.ayah', 'آية')}</span>
              <span>{t('quran.juz', 'جزء')} {surah.juz}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRead}
                className="p-2 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors group"
                title={t('common.read', 'قراءة')}
                aria-label={t('quran.readSurah', 'قراءة السورة')}
              >
                <Book className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:scale-110 transition-transform" />
              </button>
              <button
                type="button"
                onClick={handleListen}
                className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors group"
                title={t('common.listen', 'استماع')}
                aria-label={t('quran.listenSurah', 'استماع إلى السورة')}
              >
                <Headphones className="w-4 h-4 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
